import React from 'react';

import { useChatContext } from '~/components/AiChat/hooks/useChat';
import Chip from '~/components/core/Atomic/Chip/Chip';
import { WebIcon } from '~/components/icons';
import PreviewCard from '~/components/ui/atoms/PreviewCard';
import colors from '~/theme/tailwind/colors';

import { WebLinkPreview } from './WebLinkPreview';

/**
 * WebLinkTrigger
 *
 * Handles external web URL links.
 * Opens links in new tab and shows preview on hover with OG metadata when available.
 */

interface WebLinkTriggerProps {
  href: string;
  children: React.ReactNode;
}

export const WebLinkTrigger: React.FC<WebLinkTriggerProps> = ({ href, children }) => {
  const { getWebpagePreview, isWebpagePreviewLoading } = useChatContext();
  const preview = getWebpagePreview(href);
  const isLoading = isWebpagePreviewLoading(href);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const trigger = (
    <Chip
      onClick={handleClick}
      icon={<WebIcon size={16} iconColor={colors.purple[600]} />}
      label={<span className="block max-w-[114px] truncate text-xs font-semibold">{children}</span>}
      size="small"
      className="h-20 max-w-[114px] bg-purple-100 text-purple-600 hover:bg-purple-100 hover:text-purple-600"
    />
  );

  return (
    <PreviewCard trigger={trigger}>
      <WebLinkPreview url={href} preview={preview} isLoading={isLoading} />
    </PreviewCard>
  );
};
